import React, { forwardRef, MutableRefObject, useCallback, useEffect, useImperativeHandle, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
// Components
import { SelectField } from "src/components/FormikComponents/SelectField";
// Interfaces
import { IAircraft, IAircraftGroup, IUserAppSettings } from "@vmosolutions/oc-interfaces";
// Redux Actions
import { setAircraftFilters, setAircraftSort } from "src/redux/actions/flights/FlightTimeline.Actions";
import {
  IAircraftFilterOption,
  IAircraftSortOption,
  ISavedFlightDisplayOptions,
} from "src/redux/reducers/FlightTimelineReducer";

// Material-UI
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Button, Grid, Theme } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import _ from "lodash";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { IconButton } from "src/components/IconButton/IconButton";
import { updateUserAppSettings } from "src/redux/actions/App.Actions";

interface IAircraftSortFilter {
  timelineContainer?: MutableRefObject<HTMLDivElement>;
  aircraftFilters: IAircraftFilterOption[];
  filteredAircraftLabel: string;
  setTabValue?: React.Dispatch<React.SetStateAction<string | boolean>>;
  groupSave?: boolean;
}

const TimelineSortFilterStyles = (theme: Theme): any => ({
  display: "flex",
  padding: "10px",
  flexWrap: "wrap",
  alignItems: "center",
  "& .aircraft_filter_selection": {
    margin: "0px 10px",
    width: "auto",
    "& .input_control label": {
      width: "auto",
      margin: "0px 10px auto auto",
      ...theme.typography.subtitle1,
    },
    "& .react-select-container": { width: 263 }, // Older select inputs
  },
  "& .aircraft_sort_selection": {
    margin: "0px 10px",
    width: "auto",
    "& .input_control label": {
      width: "auto",
      margin: "0px 10px auto auto",
      ...theme.typography.subtitle1,
    },
    "& .react-select-container": { width: 200 }, // Older select inputs
  },
});

/**
 * If a user updated a filter group in redux, update the filter group options
 * @param filters-  current selected filters
 * @param aircraftGroups- the aircraft groups
 * @returns
 */
export const updateAircraftGroupFilterOptions = (
  filters: string[],
  options: IAircraftFilterOption[]
): IAircraftFilterOption[] => {
  const updatedFilters: IAircraftFilterOption[] = [];
  filters?.forEach((filter: string) => {
    const foundFilter = options?.find((opt: IAircraftFilterOption) => opt?.item_id === filter);
    if (foundFilter) updatedFilters.push(foundFilter);
  });
  return updatedFilters;
};

/**
 * A function that will take in the string of aircraft filters and match them to their FilterOption(if it exists)
 * @param currentFilters-  the current selected filters stored in redux
 * @param allAircraft - all aircraft
 * @param aircraftGroups - the aircraft groups stored in redux
 * @returns
 */
export const convertSelectedFiltersToFilterOptions = (
  currentFilters: string[],
  allAircraft: IAircraft[],
  aircraftGroups: IAircraftGroup[]
): IAircraftFilterOption[] => {
  //get filter options
  const aircraftTypeOptions: IAircraftFilterOption[] = createAircraftTypeGroups(allAircraft).aircraftTypeOptions;

  const aircraftSavedOptions: IAircraftFilterOption[] =
    aircraftGroups?.length > 0
      ? aircraftGroups.map((group: IAircraftGroup) => {
          return {
            label: group.title,
            value: group.members,
            count: group.members?.length ?? 0,
            group: "saved",
            item_id: group._id,
          };
        })
      : [];

  return updateAircraftGroupFilterOptions(currentFilters, [...aircraftTypeOptions, ...aircraftSavedOptions]);
};

// Sorting Values
// Default sort  of "Registration Descending" is set on line 100 of Structure.ACtions.ts
const aircraftSortOptions: IAircraftSortOption[] = [
  {
    value: "default_sorting",
    key: [],
    dir: [],
    label: "Default",
  },
  {
    value: "ds6f657e",
    key: ["aircraftType.title", "registration"],
    dir: ["asc", "asc"],
    label: "Type, Registration",
  },
  {
    value: "xf6d6123",
    key: ["aircraftType.title", "fleetNumber"],
    dir: ["asc", "asc"],
    label: "Type, Fin",
  },
  {
    value: "a23ac71a",
    key: ["registration"],
    dir: ["asc"],
    label: "Registration Ascending",
  },
  {
    value: "e0c2e9c5",
    key: ["registration"],
    dir: ["desc"],
    label: "Registration Descending",
  },
  {
    value: "4274973a",
    key: ["fleetNumber"],
    dir: ["asc"],
    label: "Fin Ascending",
  },
  {
    value: "456f6174",
    key: ["fleetNumber"],
    dir: ["desc"],
    label: "Fin Descending",
  },
];

export interface IAircraftGroupOptions {
  aircraftTypeGroups: any;
  aircraftTypeOptions: IAircraftFilterOption[];
}

/** Sort Aircraft Type title Alphabetically */
export const sortByAircraftTypeTitle = (a: IAircraft, b: IAircraft): number => {
  return b?.aircraftType?.title > a?.aircraftType?.title ? -1 : 1;
};

/**
 * Loop through all available aircraft and create groupings and options by aircraft type;
 * @param aircrafts -
 */
export const createAircraftTypeGroups = (aircrafts: IAircraft[]): IAircraftGroupOptions => {
  const tempAircrafts = [...aircrafts];
  const allOption: IAircraftFilterOption = {
    value: "all",
    label: "Select All",
    count: aircrafts.length,
    group: "types",
    item_id: "all",
  };
  const clearTypeSelection: IAircraftFilterOption = {
    value: "none",
    label: "Select None",
    count: 0,
    group: "types",
    item_id: "none",
  };
  const aircraftTypeGroups: { [key: string]: any } = {};
  const aircraftTypeOptions: IAircraftFilterOption[] = [allOption, clearTypeSelection];
  tempAircrafts.sort(sortByAircraftTypeTitle).map((AC: IAircraft) => {
    if (AC?.aircraftType?.title) {
      const ACType = AC.aircraftType.title;
      if (!aircraftTypeGroups[ACType]) {
        const ACTypeOption: IAircraftFilterOption = {
          value: AC.aircraftType.title,
          label: `${AC.aircraftType.title} - ${AC?.aircraftType?.description ?? ""}`,
          count: 0,
          group: "types",
          item_id: AC.aircraftType.title, //AC._id,
        };
        aircraftTypeOptions.push(ACTypeOption);
      }
      aircraftTypeGroups[ACType] = aircraftTypeGroups[ACType] || [];
      aircraftTypeGroups[ACType].push(AC);
      aircraftTypeOptions.forEach((item: any) => {
        if (item.value === ACType) {
          item.count = item.count + 1;
        }
      });
      return true;
    }
    return false;
  });
  return { aircraftTypeGroups, aircraftTypeOptions };
};

/**
 * Applies a selected Aircraft filter and returns associated aircraft array
 * @param allAircrafts - list containing all available aircraft
 * @param selectedACOptions - currently selected aircraft filter(s)
 */
export const applyAircraftFilter = (
  allAircrafts: IAircraft[],
  selectedACOptions: IAircraftFilterOption[]
): IAircraft[] => {
  // const selfil = selectedACOptions;
  // console.log(selfil);
  // debugger;
  const aircraftGroupOptions = createAircraftTypeGroups(allAircrafts);
  const selectedGroup = selectedACOptions?.length > 0 ? selectedACOptions[0].group : "types";

  if (selectedGroup === "saved") {
    const chosenGroup = !!selectedACOptions[0] && selectedACOptions[0].value ? selectedACOptions[0].value : "";
    const savedGroupAircraft: IAircraft[] = allAircrafts.filter((item: IAircraft) => chosenGroup.includes(item._id));
    // Sort array to match the original chosen Group before loading it into timeline.
    savedGroupAircraft.sort((a, b) => chosenGroup.indexOf(a._id) - chosenGroup.indexOf(b._id));
    return savedGroupAircraft;
  }

  if (selectedGroup === "types") {
    const filteredSelectedACTypes = selectedACOptions.filter(
      (item: any) => item.value !== "all" && item.value !== "none"
    );
    if (!!filteredSelectedACTypes && filteredSelectedACTypes.length > 0) {
      const newfilteredAircraft: IAircraft[] = [];
      for (let o = 0; o < filteredSelectedACTypes.length; o++) {
        const index = filteredSelectedACTypes[o].value as string;
        newfilteredAircraft.push(...aircraftGroupOptions.aircraftTypeGroups[index]);
      }
      return newfilteredAircraft;
    } else if (!!filteredSelectedACTypes && filteredSelectedACTypes.length === 0) {
      return [];
    }
  }
  return [];
};

export const aircraftFilterLabel = (aircraftFilters: IAircraftFilterOption[]): string => {
  // console.log("aircraftFilters", aircraftFilters);
  switch (true) {
    case aircraftFilters?.length === 0 || aircraftFilters.find((f) => f.value === "all"):
      return "All";
    case aircraftFilters?.length === 1 && aircraftFilters[0].value === "none":
      return "None";
    case aircraftFilters?.length === 1 && !!aircraftFilters[0].label:
      return aircraftFilters[0].label;
    default:
      return "Multiple Types";
  }
};

// Added iteratee functions to sort that will convert any null entries to empty strings so that null/undefined properties aren't sorted.
//!! Important addition (use lodash's get method to properly retrieve the nested value from the object) -- CC
export const sortFilteredAircraft = (aircraft: IAircraft[], sort: IAircraftSortOption): IAircraft[] => {
  const iteratees = sort?.key?.length
    ? sort?.key.map((key) => (aircraft: IAircraft) => _.get(aircraft, key) ?? "")
    : [];
  return !!iteratees?.length && sort?.dir ? _.orderBy([...aircraft], iteratees, sort.dir) : [...aircraft];
};

/**
 * Aircraft quick filter (ENG-715). Narrows an already filtered/sorted aircraft list down to the aircraft the filter
 * is pinned to. That set is a snapshot taken when the user applied the filter, not the live selection, so clicking
 * rows while filtered cannot make them disappear. When the filter is not applied the list is returned untouched so
 * every downstream consumer keeps the same reference.
 *
 * While applied the user can drag rows into a custom order ("free mode"). Aircraft named in `customOrder` come first
 * in that order; any others keep the normal sort order and follow after them.
 * @param aircraft - the aircraft list after the type/group filter and sort have been applied
 * @param filteredAircraftIds - the aircraft the filter is pinned to, or null/empty when not filtering
 * @param customOrder - optional custom vertical order set by dragging rows
 */
export const applyQuickAircraftFilter = (
  aircraft: IAircraft[],
  filteredAircraftIds: string[] | null,
  customOrder?: string[] | null
): IAircraft[] => {
  if (!filteredAircraftIds?.length || !aircraft?.length) return aircraft;
  const pinned = new Set(filteredAircraftIds);
  const visible = aircraft.filter((ac: IAircraft) => pinned.has(ac._id as string));
  if (!customOrder?.length) return visible;

  const position = new Map<string, number>();
  customOrder.forEach((id: string, index: number) => {
    if (!position.has(id)) position.set(id, index);
  });
  const ordered = visible.filter((ac: IAircraft) => position.has(ac._id as string));
  ordered.sort((a: IAircraft, b: IAircraft) => position.get(a._id as string) - position.get(b._id as string));
  const unordered = visible.filter((ac: IAircraft) => !position.has(ac._id as string));
  return [...ordered, ...unordered];
};

/**
 * The selected aircraft that the supplied list can actually show, in display order. Used so the quick filter button
 * and the select-all control only ever count aircraft the current type/group filter permits.
 */
export const selectedAircraftIdsIn = (aircraft: IAircraft[], selectedAircraftIds: string[]): string[] => {
  if (!selectedAircraftIds?.length || !aircraft?.length) return [];
  const selected = new Set(selectedAircraftIds);
  return aircraft.filter((ac: IAircraft) => selected.has(ac._id as string)).map((ac: IAircraft) => ac._id as string);
};

/** True when both lists name the same aircraft, whatever their order. */
export const sameAircraftSet = (a: string[] | null, b: string[] | null): boolean => {
  const first = a ?? [];
  const second = b ?? [];
  if (first.length !== second.length) return false;
  const inFirst = new Set(first);
  return second.every((id: string) => inFirst.has(id));
};

/**
 * Moves one aircraft to another aircraft's position in a displayed order, for drag and drop reordering in the
 * quick filter "free mode". Returns the same array when nothing would change.
 * @param displayedIds - aircraft ids in their current display order
 * @param fromId - the aircraft being dragged
 * @param toId - the aircraft whose position it should take
 */
export const moveAircraftInOrder = (displayedIds: string[], fromId: string, toId: string): string[] => {
  const fromIndex = displayedIds.indexOf(fromId);
  const toIndex = displayedIds.indexOf(toId);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return displayedIds;
  const next = [...displayedIds];
  next.splice(fromIndex, 1);
  next.splice(toIndex, 0, fromId);
  return next;
};

// Retrieve currently set saved filters from localstorage.
const getSavedFilter = (timelineAircraftFilters: any): ISavedFlightDisplayOptions | null => {
  try {
    //TODO: get these from redux
    // const savedFiltersObj: any = JSON.parse(localStorage.getItem("timeline-aircraft-filters"));
    const savedFilters =
      timelineAircraftFilters?.aircraftFilter !== undefined || timelineAircraftFilters?.aircraftSort !== undefined
        ? (timelineAircraftFilters as ISavedFlightDisplayOptions)
        : null;
    return savedFilters;
  } catch {
    console.warn("Issue loading saved aircraft filters");
    return null;
  }
};

/**
 * TIMELINE AIRCRAFT SORT AND FILTER TOOL
 * Allows to filter displayed aircraft from saved groupings or types.
 * Filtered Types can be sorted by a few different categories (saved groupings cannot be sorted for consistency in appearance).
 */
export const AircraftSortFilter = forwardRef((props: IAircraftSortFilter, ref: any): JSX.Element => {
  const { timelineContainer, aircraftFilters, filteredAircraftLabel, setTabValue, groupSave = false } = props;

  const theme = useTheme();
  const dispatch = useDispatch();

  // Filter Values
  const allAircrafts = useSelector((state: IRootState) => state.structures.customerStructure.aircraft);

  const { aircraftTypeGroups, aircraftTypeOptions }: IAircraftGroupOptions = useMemo(() => {
    return createAircraftTypeGroups(allAircrafts);
  }, [allAircrafts]);

  // Store savedFilter loaded in from localstorage here to speed up the boolean when it's set.
  const [savedFilters, setSavedFilters] = React.useState<ISavedFlightDisplayOptions>(null);
  const appSettings: IUserAppSettings | null = useSelector((state: IRootState) => state.authentication.appSettings);

  useEffect(() => {
    const getSettings = (timelineAircraftFilters: any): void => {
      const settings = getSavedFilter(timelineAircraftFilters);
      if (settings) {
        setSavedFilters(settings);
      }
    };
    getSettings(appSettings?.timelineAircraftFilters);
  }, [appSettings]);

  const aircraftSort = useSelector((state: IRootState) => state.timelineFlights.aircraftSort);
  const aircraftGroups = useSelector((state: IRootState) => state.structures.customerStructure.aircraftGroups);
  const [selectedGroup, setSelectedGroup] = React.useState<string>(null);
  const [filterGroupLabel] = React.useState<string>("Types");

  // Set default selection to display all options except for none.
  const defaultTypeSelection: IAircraftFilterOption[] = useMemo(() => {
    return aircraftTypeOptions?.filter((option) => option.value !== "none") ?? [];
  }, [aircraftTypeOptions]);

  /**
   * Map supplied aircraftGroup structure into a format ususable for our selection options.
   * @param aircraftGroups -
   */
  const aircraftSavedOptions: IAircraftFilterOption[] = useMemo(() => {
    return aircraftGroups?.length > 0
      ? aircraftGroups.map((group: IAircraftGroup) => {
          return {
            label: group.title,
            value: group.members,
            count: group.members?.length ?? 0,
            group: "saved",
            item_id: group._id,
          };
        })
      : [];
  }, [aircraftGroups]);

  // These groupings will be saved/supplied by the database for each customer.
  const selectionGroupedOptions: any = useMemo(
    () => [
      { label: "Custom Aircraft Groups", options: aircraftSavedOptions },
      { label: "Aircraft Types", options: aircraftTypeOptions },
    ],
    [aircraftSavedOptions, aircraftTypeOptions]
  );

  // Apply selected filter based on redux value or default if not found.
  const selectedFilters = useMemo(() => {
    return aircraftFilters?.length > 0 ? aircraftFilters : defaultTypeSelection;
  }, [aircraftFilters, defaultTypeSelection]);

  // console.log("aircraftTypeOptions", aircraftTypeOptions);
  // console.log("aircraftSavedOptions", aircraftSavedOptions);
  // console.log("selectedFilters", selectedFilters);

  // On mount, use the default aircraft list to create the aircraft type group object and options array.
  useEffect(() => {
    if (!!allAircrafts && allAircrafts.length > 0) createAircraftTypeGroups(allAircrafts);
  }, [allAircrafts]);

  // set parent element for selection menu portals.
  /*
  useEffect(() => {
    if (!!timelineContainer && !!timelineContainer.current) {
      setParentElement(timelineContainer.current);
    }
  }, [timelineContainer]);
  */

  // Loops through the key names for the the available type groups and returns true if they are all selected.
  const checkIfAllTypesSelected = (selection: any): boolean => {
    let allSelected = true;
    Object.keys(aircraftTypeGroups).forEach((key, index) => {
      const group = selection.find((item: any) => item.value === key);
      if (!group) {
        allSelected = false;
      }
    });
    return allSelected;
  };

  // Loops through the key names for the the available type groups and returns true if they are all unselected.
  const checkIfAllTypesUnSelected = (selection: any): boolean => {
    let allUnSelected = true;
    Object.keys(aircraftTypeGroups).forEach((key, index) => {
      const group = selection.find((item: any) => item.value === key);
      if (group) {
        allUnSelected = false;
      }
    });
    return allUnSelected;
  };

  /**
   * Stores the selected aircraft type selections. (doesn't apply them yet)
   * @param event -
   */
  const handleFilterAircraftRows = (event: any[]): void => {
    if (!event) {
      return;
    }
    const lastItemClicked = event.length > 1 ? event[event.length - 1] : event[0];
    console.log(lastItemClicked);
    if (!lastItemClicked) {
      return;
    }
    const optionGroupSelected = lastItemClicked.group;
    if (optionGroupSelected === "saved") {
      setSelectedGroup("saved");
      // If a saved group is chosen, reset sorting to it's default setting as we want these groups displayed in the order they were created.
      dispatch(
        setAircraftSort({
          value: "default_sorting",
          key: [],
          dir: [],
          label: "Default",
        })
      );
      // Only pass the last item clicked for saved (as we don't allow multiple saved groups displayed at once).
      dispatch(setAircraftFilters([lastItemClicked?.item_id]));
    }
    if (optionGroupSelected === "types") {
      const typesSelection = event.filter((item: any) => item.group === optionGroupSelected);
      setSelectedGroup("types");
      typesSelected(typesSelection, lastItemClicked);
    }
  };

  // User chose one or more aircraft types.
  const typesSelected = (event: any[], lastItemClicked: any): void => {
    if ((checkIfAllTypesUnSelected(event) && lastItemClicked.value !== "all") || lastItemClicked.value === "none") {
      // SELECT NONE
      dispatch(setAircraftFilters([aircraftTypeOptions[1]?.item_id]));
    } else if (
      (checkIfAllTypesSelected(event) && lastItemClicked.value !== "none") ||
      lastItemClicked.value === "all"
    ) {
      // const selectAll = aircraftTypeOptions.filter((item: any, index: number) => item.value !== "none");
      // Save selectAll as an empty
      dispatch(setAircraftFilters([]));
    } else {
      // SELECT TYPES
      const selection = event.filter((item: any) => item.value !== "all" && item.value !== "none");
      dispatch(setAircraftFilters(selection?.map((select: any) => select.item_id)));
      let placeholderString = `${selection[0].value},`;
      for (let o = 1; o < selection.length; o++) {
        placeholderString = `${placeholderString} ${selection[o].value},`;
      }
    }
  };

  const matchesSaved = useMemo(() => {
    return _.isEqual(
      { aircraftFilter: aircraftFilters?.map((filter) => filter?.item_id), aircraftSort: aircraftSort },
      {
        aircraftFilter: savedFilters?.aircraftFilter,
        aircraftSort: savedFilters?.aircraftSort,
      }
    );
  }, [aircraftFilters, savedFilters, aircraftSort]);

  const saveAsMyDefault = useCallback(async () => {
    const newValue = {
      aircraftFilter: aircraftFilters.map((filter: IAircraftFilterOption) => filter.item_id),
      aircraftSort: aircraftSort,
    } as ISavedFlightDisplayOptions;
    // localStorage.setItem("timeline-aircraft-filters", JSON.stringify(newValue));
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Saving Aircraft Filter/Sort as Default...",
      successMessage: "Default Aircraft Filter/Sort Saved Successfully.",
      errorMessage: "Error: Unable to Save Aircraft Filter/Sort",
    };
    const response = await AmplifyAPIWrapper.Put<any>(
      "/me/settings",
      {
        timelineAircraftFilters: newValue,
      },
      !groupSave && notifications
    );
    dispatch(updateUserAppSettings(response, "timelineAircraftFilters"));
    setSavedFilters(newValue);
  }, [aircraftFilters, aircraftSort, dispatch, groupSave]);

  useImperativeHandle(ref, () => ({
    saveDefault() {
      const newValue = {
        aircraftFilter: aircraftFilters.map((filter: IAircraftFilterOption) => filter.item_id),
        aircraftSort: aircraftSort,
      } as ISavedFlightDisplayOptions;

      setSavedFilters(newValue);
      return newValue;
    },
  }));

  /** If custom groups are updated and the actively selected type has been changed, update it. */
  useEffect(() => {
    if (aircraftFilters.length === 1 && aircraftSavedOptions.length > 0) {
      const { group, item_id, value, label } = aircraftFilters[0];
      // Return item only if group and id match and value or label have been updated.
      const updatedACType = aircraftSavedOptions.find((i) => {
        return i.item_id === item_id && i.group === group && (i.label !== label || !_.isEqual(i.value, value));
      });
      if (updatedACType) {
        dispatch(setAircraftFilters([updatedACType?.item_id]));
      }
    }
  }, [aircraftFilters, aircraftSavedOptions, dispatch]);

  // Sort the Aircraft array and re-assign to redux store.
  const handleSortAircraftRows = useCallback(
    (sort: IAircraftSortOption): void => {
      if (!sort?.hasOwnProperty("key") || !sort?.hasOwnProperty("dir")) {
        return;
      }
      dispatch(setAircraftSort(sort));
    },
    [dispatch]
  );

  useEffect(() => {
    if (aircraftFilters?.length == 0) setSelectedGroup(null);
  }, [aircraftFilters]);

  return (
    <Grid container sx={TimelineSortFilterStyles(theme)}>
      {setTabValue && (
        <IconButton style={{ paddingBottom: "4px" }} onClick={() => setTabValue(false)}>
          <VisibilityOffIcon fontSize="small" />
        </IconButton>
      )}
      <SelectField
        className="aircraft_filter_selection"
        name="selectedACFilter"
        label="Filter Aircraft"
        getOptionLabel={(option: any) => `${option.label} (${option.count})`}
        getOptionValue={(option: any) => option.value}
        // Commenting our unded properties.
        // isOptionSelected={(option: any, selection: any) => selection.includes(option)}
        // closeMenuOnSelect={false}
        //
        // menuPortalTarget={parentElement}
        isMulti={true}
        isSearchable={true}
        isClearable={true}
        value={selectedFilters}
        options={selectionGroupedOptions}
        onChange={handleFilterAircraftRows}
        placeholder={`${filterGroupLabel}: ${filteredAircraftLabel}`}
        menuPosition="fixed"
        maxMenuHeight={400}
        multiMode="checkbox"
      />

      {selectedGroup !== "saved" &&
        !selectedFilters?.find((filter: IAircraftFilterOption) => filter?.group === "saved") && (
          <SelectField
            className="aircraft_sort_selection"
            name="sortingMethod"
            label={
              selectedGroup === "saved" ? "Sorting for saved groups modified in the admin section" : "Sort Aircraft"
            }
            getOptionLabel={(option: any) => option.label}
            getOptionValue={(option: any) => option.value}
            isMulti={false}
            closeMenuOnSelect={true}
            isSearchable={true}
            isClearable={false}
            value={aircraftSort?.value ? aircraftSort : aircraftSortOptions[0]}
            options={aircraftSortOptions}
            onChange={handleSortAircraftRows}
            // value={aircraftSort ?? aircraftSortOptions[0]}
            // menuPortalTarget={parentElement}
            // placeholder="sorting method..."
            placeholder={aircraftSort?.label ?? "Default"}
            menuPosition="fixed"
            maxMenuHeight={400}
            multiMode="checkbox"
          />
        )}
      {!groupSave && (
        <Button
          id={"deselect_btn"}
          disabled={matchesSaved}
          variant="outlined"
          onClick={saveAsMyDefault}
          className="save_as_default_filter"
        >
          {matchesSaved ? "Saved as default" : "Save as my default"}
        </Button>
      )}
    </Grid>
  );
});
